import DOMPurify from 'dompurify';

/**
 * Sanitizes untrusted HTML before it is rendered via `dangerouslySetInnerHTML`.
 *
 * API-sourced HTML (boat descriptions, AI-generated content, SEO content and
 * tenant-authored templates) is rich text that we want to display with its
 * formatting intact — but it must never be able to execute script. This helper
 * runs DOMPurify with the standard HTML profile and an extra hardening config:
 *
 *  - `USE_PROFILES: { html: true }` — allows the common rich-text tag set
 *    (`<p>`, `<strong>`, `<em>`, `<ul>`, `<a>`, headings, tables, etc.) while
 *    excluding SVG/MathML attack surface.
 *  - `FORBID_TAGS` — explicitly drops `<script>`, `<iframe>`, `<object>`,
 *    `<embed>`, `<form>` and friends even if a future profile change would
 *    otherwise permit them.
 *  - `FORBID_ATTR` — strips inline event handlers (`onerror`, `onclick`, …)
 *    and `style` so attribute-based script execution is not possible.
 *  - DOMPurify's default `ALLOWED_URI_REGEXP` already neutralizes
 *    `javascript:` and executable `data:` URLs in `href`/`src`.
 *
 * Always route untrusted HTML through this function. Never pass raw API HTML
 * straight into `dangerouslySetInnerHTML`.
 */
export function sanitizeHtml(dirty: string): string {
  if (typeof dirty !== 'string' || dirty.length === 0) {
    return '';
  }

  return DOMPurify.sanitize(dirty, {
    USE_PROFILES: { html: true },
    // Defense-in-depth: drop dangerous elements outright.
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'base', 'style'],
    // Drop all inline event handlers and inline styles.
    FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick', 'onmouseover', 'onfocus'],
    // Disallow any custom URI protocols (only http/https/mailto/tel/ftp + relative).
    ALLOW_UNKNOWN_PROTOCOLS: false,
  });
}

/**
 * Sanitizes an untrusted SVG document before it is rendered via
 * `dangerouslySetInnerHTML`.
 *
 * The 2FA setup flow renders a QR-code SVG returned by the backend. Even
 * though that SVG is server-generated, an SVG is an active document — it can
 * carry `<script>`, `<foreignObject>` and event-handler attributes — so it
 * must not be injected into the DOM raw. This helper runs DOMPurify with the
 * SVG profile and drops the scripting attack surface while preserving the
 * shape elements (`<svg>`, `<path>`, `<rect>`, …) the QR code is drawn from.
 */
export function sanitizeSvg(dirty: string): string {
  if (typeof dirty !== 'string' || dirty.length === 0) {
    return '';
  }

  return DOMPurify.sanitize(dirty, {
    USE_PROFILES: { svg: true, svgFilters: true },
    // Defense-in-depth: <script>/<foreignObject> are the SVG scripting vectors.
    FORBID_TAGS: ['script', 'foreignObject'],
    FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onfocus'],
    ALLOW_UNKNOWN_PROTOCOLS: false,
  });
}
